import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Compare table with a sticky header. Per-row tooltip on every label.
 * Phone layout: a small plan-tab row at the top of the table hides
 * the other two columns via a CSS class applied by the parent.
 */
import { useState } from 'react';
import { mono, sans, serif } from '../fonts.js';
import { labelFromSlug } from './copy-deck.js';
import { formatLimit, formatMoneyMinor, formatPrice, pickPrice, } from './format.js';
import { Tooltip } from './Tooltip.js';
import { CheckMark, DashMark } from './icons.js';
function cellForFeature(plan, featureSlug, theme) {
    const hasIt = plan.features.includes(featureSlug);
    return hasIt ? (_jsx("span", { style: { color: theme.limeFg }, "aria-label": "Included", children: _jsx(CheckMark, {}) })) : (_jsx("span", { style: { color: theme.dim }, "aria-label": "Not included", children: _jsx(DashMark, {}) }));
}
function cellForDimension(plan, dimension) {
    const lim = plan.limits.find((l) => l.dimension === dimension);
    if (lim === undefined)
        return '—';
    return formatLimit(lim);
}
export function CompareTable({ plans, theme, interval, copy, ctaHref, monthsFreeOnYearly = 2, }) {
    // Which column to show on phones. Default = the second plan (the
    // recommended one in the 3-column layout). Resolved to the free
    // plan when there are < 2 plans.
    const [shownPhonePlan, setShownPhonePlan] = useState(plans.length >= 2 ? (plans[1]?.slug ?? plans[0]?.slug ?? '') : (plans[0]?.slug ?? ''));
    if (plans.length === 0)
        return _jsx(_Fragment, {});
    const groups = (copy.compareSections ?? []).map((g) => g);
    // Append a "Features" group of feature slugs that the catalog
    // actually carries on at least one plan, so a staff-added feature
    // surfaces in the compare table without a copy-deck edit.
    const featureSlugs = Array.from(new Set(plans.flatMap((p) => p.features))).filter((s) => copy.features[s] !== undefined);
    if (featureSlugs.length > 0) {
        groups.push({ kicker: 'Features', rows: featureSlugs });
    }
    const corner = {
        padding: '14px 16px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
        boxShadow: '0 6px 14px -12px rgba(0,0,0,0.2)',
        position: 'sticky',
        top: 0,
        zIndex: 2,
        verticalAlign: 'bottom',
    };
    const th = {
        ...corner,
        textAlign: 'left',
    };
    const td = {
        padding: '11px 16px',
        borderBottom: `1px solid ${theme.borderSoft}`,
        ...sans,
        fontSize: 14,
        verticalAlign: 'middle',
    };
    const groupTh = {
        ...mono,
        fontSize: 11,
        letterSpacing: 1.8,
        textTransform: 'uppercase',
        color: theme.text,
        background: theme.surface2,
        fontWeight: 500,
        padding: '9px 16px',
        textAlign: 'left',
        borderBottom: `1px solid ${theme.borderSoft}`,
    };
    return (_jsx("div", { style: {
            marginTop: 56,
            border: `1px solid ${theme.border}`,
            borderRadius: 4,
            background: theme.surface,
            overflow: 'clip',
        }, children: _jsxs("table", { className: `pr-compare pr-compare-${shownPhonePlan}`, "data-show": shownPhonePlan, style: {
                width: '100%',
                borderCollapse: 'separate',
                borderSpacing: 0,
            }, children: [_jsx("thead", { children: _jsxs("tr", { children: [_jsx("th", { scope: "col", className: "pr-cmp-corner", style: { ...th, width: '34%' }, children: _jsxs("div", { style: { display: 'grid', gap: 6 }, children: [_jsx("span", { className: "pr-cmp-kicker", style: {
                                                ...mono,
                                                fontSize: 11,
                                                letterSpacing: 1.8,
                                                textTransform: 'uppercase',
                                                color: theme.muted,
                                            }, children: "Compare plans" }), _jsx("div", { className: "pr-ptabs", role: "group", "aria-label": "Plan to show", style: {
                                                display: 'none',
                                                border: `1px solid ${theme.border}`,
                                                borderRadius: 3,
                                                padding: 2,
                                                background: theme.surface,
                                            }, children: plans.map((p) => {
                                                const on = p.slug === shownPhonePlan;
                                                return (_jsx("button", { type: "button", "aria-pressed": on, onClick: () => setShownPhonePlan(p.slug), style: {
                                                        ...sans,
                                                        border: 0,
                                                        background: on ? theme.text : 'transparent',
                                                        color: on ? theme.bg : theme.muted,
                                                        padding: '5px 8px',
                                                        borderRadius: 2,
                                                        fontSize: 13,
                                                        cursor: 'pointer',
                                                    }, children: p.name }, p.plan_id));
                                            }) })] }) }), plans.map((p, idx) => {
                                const price = pickPrice(p.prices, interval);
                                const monthly = pickPrice(p.prices, 'monthly');
                                const yearlyPrice = pickPrice(p.prices, 'yearly');
                                let shownMonthly = price?.amount_minor ?? 0;
                                if (interval === 'yearly'
                                    && yearlyPrice !== undefined
                                    && yearlyPrice.amount_minor > 0) {
                                    const effectiveMonths = Math.max(1, 12 - monthsFreeOnYearly);
                                    shownMonthly = Math.round(yearlyPrice.amount_minor / effectiveMonths);
                                }
                                const isFree = (monthly?.amount_minor ?? 0) === 0;
                                const featured = idx === 1 && plans.length >= 3;
                                return (_jsx("th", { scope: "col", className: `pr-c-${p.slug}`, style: th, children: _jsxs("div", { style: { display: 'grid', gap: 6 }, children: [_jsx("span", { style: {
                                                    ...serif,
                                                    fontSize: 22,
                                                    color: theme.text,
                                                    lineHeight: 1,
                                                    fontWeight: 400,
                                                }, children: p.name }), _jsxs("span", { style: {
                                                    ...sans,
                                                    fontWeight: 600,
                                                    fontSize: 18,
                                                    color: theme.text,
                                                    fontVariantNumeric: 'tabular-nums',
                                                }, children: [price === undefined
                                                        ? '—'
                                                        : formatPrice({ ...price, amount_minor: shownMonthly }), isFree ? ('') : (_jsxs("span", { style: { fontWeight: 400, fontSize: 12, color: theme.muted }, children: [' ', "/site a month"] }))] }), _jsx("a", { href: ctaHref, style: {
                                                    ...sans,
                                                    display: 'inline-block',
                                                    textAlign: 'center',
                                                    border: `1px solid ${featured ? theme.lime : theme.border}`,
                                                    background: featured ? theme.lime : theme.surface,
                                                    color: featured ? theme.ink : theme.text,
                                                    borderRadius: 2,
                                                    padding: '6px 10px',
                                                    fontSize: 13,
                                                    textDecoration: 'none',
                                                    fontWeight: 500,
                                                }, children: isFree ? copy.ctaFree : `Start with ${p.name}` }), interval === 'yearly' && !isFree && yearlyPrice !== undefined ? (_jsxs("span", { style: { ...sans, fontSize: 11, color: theme.muted }, children: ["Billed ", formatMoneyMinor(yearlyPrice.amount_minor, yearlyPrice.currency), " / yr"] })) : null] }) }, p.plan_id));
                            })] }) }), groups.map((g) => {
                    const visible = g.rows.filter((row) => copy.dimensions[row] !== undefined
                        || copy.features[row] !== undefined);
                    if (visible.length === 0)
                        return null;
                    return (_jsxs("tbody", { children: [_jsx("tr", { className: "pr-cmp-group", children: _jsx("th", { colSpan: plans.length + 1, scope: "colgroup", style: groupTh, children: g.kicker }) }), visible.map((row) => {
                                const isDimension = copy.dimensions[row] !== undefined;
                                const deck = isDimension ? copy.dimensions[row] : copy.features[row];
                                const label = deck?.label ?? labelFromSlug(row);
                                const tip = deck?.tooltip ?? '';
                                return (_jsxs("tr", { children: [_jsx("td", { style: { ...td, color: theme.text }, children: _jsx(Tooltip, { tip: tip, theme: theme, children: label }) }), plans.map((p) => {
                                            const content = isDimension
                                                ? cellForDimension(p, row)
                                                : cellForFeature(p, row, theme);
                                            return (_jsx("td", { className: `pr-c-${p.slug}`, style: { ...td, color: theme.body }, children: content }, `${p.plan_id}-${row}`));
                                        })] }, `${g.kicker}-${row}`));
                            })] }, `group-${g.kicker}`));
                })] }) }));
}
//# sourceMappingURL=CompareTable.js.map